<i18n>
{
  "en": {
    "title": "Schneider Sustainability Report 2017 – 2018",
    "description": "Read the annual report covering Schneider Electric’s sustainable development, corporate social responsibility, climate change and environment strategy, renewable energy sources, digital innovation, EcoStruxure platform, IoT and energy efficiency products.",
    "homepage": {
      "cta": {
        "text": "Read chapter"
      },
      "introduction": {
        "title": "A clear vision for <span class='t-nowrap'>the future</span>",
        "subtitle": "Sustainability is driving growth in Shenzhen, China's greenest city."
      },
      "chapter1": {
        "title": "Introduction:<br />Our Mission",
        "subtitle": "The digital transformation of energy management and automation is our mission. As our CEO, Jean-Pascal Tricoire says, “When it comes to climate change, I’m neither an optimist nor a pessimist. I’m an activist.”"
      },
      "chapter2": {
        "title": "Sustainable<br />Solutions",
        "subtitle": "We believe meaningful action is the only path to a sustainable world. Discover how we use EcoStruxure&trade; innovation and open collaboration to create efficient, effective, green solutions for our customers."
      },
      "chapter3": {
        "title": "Our<br />Customers",
        "subtitle": "Across buildings, data centers, industry, and infrastructure, we’re equipping the world with connected solutions that increase operational and energy efficiency &mdash; and make sustainability a reality. See the results."
      },
      "chapter4": {
        "title": "Our<br />People",
        "subtitle": "It takes great people to power our sustainability efforts and our company. We offer a learning-focused, inclusive workplace that fosters innovation and fuels professional development. Learn more about our shared, meaningful purpose."
      },
      "chapter5": {
        "title": "Outreach and<br />Initiatives",
        "subtitle": "Today, 1.1 billion people lack access to electricity. As we work to reduce the energy gap and address fuel poverty, we must do so responsibly. Learn how we’re innovating to solve the energy paradox."
      },
      "chapter6": {
        "title": "Company<br />Performance",
        "subtitle": "We launched more than 100 new products, continued to optimize our portfolio, and delivered growth for shareholders. See how Schneider Electric performed in 2017."
      },
      "chapter7": {
        "title": "Looking<br />Ahead",
        "subtitle": "We’re taking proactive steps toward co-creating a sustainable future with immediate and short- and long-term sustainability targets. See what’s next."
      },
      "references": {
        "referenceA": "<sup>1</sup> International Energy Agency, Energy Access Outlook 2017"
      }
    }
  },
  "fr": {
    "title": "Schneider Sustainability Report 2017 – 2018",
    "description": "Read the annual report covering Schneider Electric’s sustainable development, corporate social responsibility, climate change and environment strategy, renewable energy sources, digital innovation, EcoStruxure platform, IoT and energy efficiency products.",
    "homepage": {
      "cta": {
        "text": "En savoir plus"
      },
      "introduction": {
        "title": "Une vision claire pour le futur.",
        "subtitle": "Le développement durable, moteur de la croissance à Shenzhen, la ville la plus verte de Chine."
      },
      "chapter1": {
        "title": "Introduction:<br />Notre Mission",
        "subtitle": "Le développement durable est une démarche au coeur de la stratégie de Schneider Electric. Écoutez notre PDG, consultez nos plans de progrès en matière de développement durable et découvrez notre nouveau baromètre Schneider Sustainability Impact pour la période 2018 &ndash; 2020."
      },
      "chapter2": {
        "title": "Solutions<br />Durables",
        "subtitle": "Nous pensons que l'action engagée est la seule voie vers un monde durable. Découvrez comment nous misons sur les innovations EcoStruxure<sup>&trade;</sup> et sur la collaboration ouverte pour créer des solutions efficaces, économiques et écologiques pour nos clients."
      },
      "chapter3": {
        "title": "Nos<br />Clients",
        "subtitle": "Bâtiments, centres de données, industries, infrastructures ... Dans tous ces domaines, nous équipons le monde de solutions connectées propres à accroître l’efficacité opérationnelle et énergétique – pour que la durabilité devienne réalité. Découvrez nos résultats."
      },
      "chapter4": {
        "title": "Nos<br />Collaborateurs",
        "subtitle": "Pour alimenter nos efforts en matière de développement durable et faire avancer notre entreprise, il nous faut des collaborateurs motivés. Nous leur proposons un environnement de travail inclusif et axé sur la formation, qui stimule l’innovation et nourrit le développement personnel. Découvrez la raison d’être qui anime chacune et chacun d’entre nous."
      },
      "chapter5": {
        "title": "Initiatives<br /> et Communautés",
        "subtitle": "Aujourd'hui, 1,1 milliard de personnes n’ont pas accès à l’électricité. Nous devons œuvrer de manière responsable à réduire la fracture énergétique et à lutter contre la précarité énergétique. Découvrez les innovations que nous mettons en place pour résoudre le paradoxe énergétique."
      },
      "chapter6": {
        "title": "Performance<br />de l’entreprise",
        "subtitle": "Nous avons lancé plus de 100 nouveaux produits, avons réalisé des acquisitions d’envergure, et créé de la croissance pour nos actionnaires. Découvrez toutes les performances de Schneider Electric en 2017."
      },
      "chapter7": {
        "title": "Anticiper<br />Demain",
        "subtitle": "Nous agissons de manière proactive pour co-créer un avenir durable grâce à des objectifs de développement durable allant du très court terme au long terme. Découvrez le monde de demain."
      },
      "references": {
        "referenceA": "<sup>1</sup> International Energy Agency, Energy Access Outlook 2017"
      }
    }
  }
}
</i18n>

<template>
  <div id="Home">
    <div class="hero">
      <div class="l-content">
        <div class="col-xs-16 col-xs-offset-1">
          <div class="hero-content">
            <h1>Schneider Sustainability Report</h1>
            <div class="title">
              <h2 class="pagetitle" v-html="$t('homepage.introduction.title')"></h2>
              <p v-html="$t('homepage.introduction.subtitle')"></p>
            </div>
            <a class="hero__scroll" @click="scrollDown"></a>
          </div>
        </div>
      </div>
    </div>
    <!-- <HomeSection id="hero" :bgImage="require('@/assets/img/homepage/landing.jpg')">
      <h1>A clear vision for the future.</h1>
      <p>Sustainability is driving growth in Shenzhen, China's greenest city.</p>
    </HomeSection> -->
    <HomeSection id="introduction" :bgImage="require('@/assets/img/homepage/introduction.jpg')">
      <h2 v-html="$t('homepage.chapter1.title')"></h2>
      <p class="t-body" v-html="$t('homepage.chapter1.subtitle')"></p>
      <CTARoute route="Introduction_Welcome" v-html="$t('homepage.cta.text')"></CTARoute>
    </HomeSection>
    <HomeSection id="sustainable" :bgImage="require('@/assets/img/homepage/sustainable.jpg')">
      <h2 v-html="$t('homepage.chapter2.title')"></h2>
      <p class="t-body" v-html="$t('homepage.chapter2.subtitle')"></p>
      <CTARoute route="SustainableSolutions_Overview" v-html="$t('homepage.cta.text')"></CTARoute>
    </HomeSection>
    <HomeSection id="customers" :bgImage="require('@/assets/img/homepage/our-customers.jpg')">
      <h2 v-html="$t('homepage.chapter3.title')"></h2>
      <p class="t-body" v-html="$t('homepage.chapter3.subtitle')"></p>
      <CTARoute route="OurCustomers_Overview" v-html="$t('homepage.cta.text')"></CTARoute>
    </HomeSection>
    <HomeSection id="people" :bgImage="require('@/assets/img/homepage/our-people.jpg')">
      <h2 v-html="$t('homepage.chapter4.title')"></h2>
      <p class="t-body" v-html="$t('homepage.chapter4.subtitle')"></p>
      <CTARoute route="OurPeople_Overview" v-html="$t('homepage.cta.text')"></CTARoute>
    </HomeSection>
    <HomeSection id="outreach" :bgImage="require('@/assets/img/homepage/outreach.jpg')">
      <h2 v-html="$t('homepage.chapter5.title')"></h2>
      <p class="t-body" v-html="$t('homepage.chapter5.subtitle')"></p>
      <CTARoute route="Outreach_Overview" v-html="$t('homepage.cta.text')"></CTARoute>
    </HomeSection>
    <HomeSection id="company" :bgImage="require('@/assets/img/homepage/company-performance.jpg')">
      <h2 v-html="$t('homepage.chapter6.title')"></h2>
      <p class="t-body" v-html="$t('homepage.chapter6.subtitle')"></p>
      <CTARoute route="CompanyPerformance_Overview" v-html="$t('homepage.cta.text')"></CTARoute>
    </HomeSection>
    <HomeSection id="looking" :bgImage="require('@/assets/img/homepage/looking-forward.jpg')">
      <h2 v-html="$t('homepage.chapter7.title')"></h2>
      <p class="t-body" v-html="$t('homepage.chapter7.subtitle')"></p>
      <CTARoute route="LookingAhead_Overview" v-html="$t('homepage.cta.text')"></CTARoute>
    </HomeSection>
    <!-- <References>
      <Reference>
        <div v-html="$t('homepage.references.referenceA')"></div>
      </Reference>
    </References> -->
  </div>
</template>

<script>
import HomeSection from '@/components/HomeSection'
import YouTubeVideo from '@/components/YouTubeVideo'
import Pillar from '@/components/Pillar'
import Quote from '@/components/Quote'
import CTA from '@/components/CTA'
import TxtMedia from '@/components/TxtMedia'
import Footer from '@/components/Footer'
import CTARoute from '@/components/CTARoute'
import References from '@/components/References'
import Reference from '@/components/Reference'
// import { TimelineMax, TweenMax, Linear } from 'gsap'
import ScrollMagic from 'scrollmagic'
import 'animation.gsap'
import 'debug.addIndicators'
import 'ScrollToPlugin'

export default {
  name: 'Homepage',
  components: {
    YouTubeVideo,
    Pillar,
    Quote,
    CTA,
    TxtMedia,
    Footer,
    HomeSection,
    CTARoute,
    References,
    Reference
  },
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  data: () => ({
    controller: null,
    scene: null
  }),
  mounted () {
    if (window.innerWidth > 768) { // Only open on larger screens
      this.$root.menuOpen = true
    }

    this.$nextTick(function () {
      this.controller = new ScrollMagic.Controller({globalSceneOptions: { triggerHook: 'onEnter', duration: '200%' }})
      // DO NOT REMOVE
      // this.scene = new ScrollMagic.Scene({ triggerElement: '#hero' }).setTween('#hero .pbg', { y: '80%', ease: Linear.easeNone }).addTo(this.controller)
      this.scene = new ScrollMagic.Scene({ triggerElement: '#introduction' }).setTween('#introduction .pbg', { y: '40%', ease: Linear.easeNone }).addTo(this.controller)
      this.scene = new ScrollMagic.Scene({ triggerElement: '#sustainable' }).setTween('#sustainable .pbg', { y: '40%', ease: Linear.easeNone }).addTo(this.controller)
      this.scene = new ScrollMagic.Scene({ triggerElement: '#customers' }).setTween('#customers .pbg', { y: '40%', ease: Linear.easeNone }).addTo(this.controller)
      this.scene = new ScrollMagic.Scene({ triggerElement: '#people' }).setTween('#people .pbg', { y: '40%', ease: Linear.easeNone }).addTo(this.controller)
      this.scene = new ScrollMagic.Scene({ triggerElement: '#outreach' }).setTween('#outreach .pbg', { y: '40%', ease: Linear.easeNone }).addTo(this.controller)
      this.scene = new ScrollMagic.Scene({ triggerElement: '#company' }).setTween('#company .pbg', { y: '40%', ease: Linear.easeNone }).addTo(this.controller)
      this.scene = new ScrollMagic.Scene({ triggerElement: '#looking' }).setTween('#looking .pbg', { y: '40%', ease: Linear.easeNone }).addTo(this.controller)
    })

    // DO NOT REMOVE
    // var $window = $(window)
    // var scrollTime = 1.2
    // // scrollDistaince = 185
    // var scrollDistance = 8
    //
    // $window.on('wheel', function (event) {
    //   // mousewheel DOMMouseScroll
    //   console.log('as;fljsa;lajlja;jf')
    //   console.log(event.originalEvent.wheelDelta)
    //   console.log(-event.originalEvent.detail)
    //   event.preventDefault()
    //   // event.originalEvent.wheelDelta || -event.originalEvent.detail
    //   //  / 120
    //   var delta = -event.originalEvent.deltaY
    //   var scrollTop = $window.scrollTop()
    //   var finalScroll = scrollTop - parseInt(delta * scrollDistance)
    //   TweenMax.to($window, scrollTime, {
    //     scrollTo: { y: finalScroll, autoKill: true },
    //     ease: Power1.easeOut,
    //     overwrite: 5
    //   })
    // })
  },
  beforeUpdate () {

  },
  beforeDestroy () {
    // var $window = $(window)
    // $window.off()
    this.controller.destroy(1)
    this.controller = null
  },
  methods: {
    scrollDown () {
      const targetEl = document.getElementById('introduction')
      if (targetEl) {
        this.$scrollTo(targetEl, 1000)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/vars';
.hero {
  position: relative;
  display: block;
  height: calc(100vh - 50px);
  min-height: 500px;
  max-width: 100vw;
  background-size: 100%;
  background-size: cover;
  background-position: bottom center;
  background-image: url('../assets/img/homepage/landing-mobile.jpg');
  // background-image: url('../assets/img/homepage/GettyImages-803527592_color_SUSTAINABILITY.jpg');
  text-align: center;
  @media(min-width: 992px) {
    // background-image: url('../assets/img/homepage/landing.jpg');
    // background-image: url('../assets/img/homepage/GettyImages-803527592_color_SUSTAINABILITY.jpg');
      background-image: url('../assets/img/homepage/landing-desktop.jpg');

  }
  @media (min-width: 1200px) and (max-height: 850px) {
    // background-image: url('../assets/img/homepage/landing-desktop.jpg');
    // background-image: url('../assets/img/homepage/GettyImages-803527592_color_SUSTAINABILITY.jpg');
  }
  .hero-content {
    height: calc(100vh - 50px);
    top: 0;
    left: 0;
    width: 100%;
    color: white;
    display: flex;
    flex-direction: column;
    // align-items: flex-end;
    justify-content: flex-start;
    position: relative;
    h1 {
      visibility: hidden;
      display: block;
      padding-left: 30px;
      padding-right: 30px;
      padding: 16px 20px;
      font-size: 21px;
      font-weight: 300;
      @media(min-width: 1200px) {
        visibility: visible;
        display: block;
        width: auto;
        max-width: 500px;
        margin: 0 auto;
        color: #3dcd58;
        background-color: white;
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      }
    }
    .title {
      // align-self: flex-end;
      margin-top: 50vh;
      @media (min-width: 768px) {
        margin-top: 65vh;
      }
      @media only screen and (min-device-height: 668px) and (max-device-height: 800px) {
        margin-top: 55vh;
      }
    }
    h2.pagetitle {
      font-size: 32px;
      line-height: 34px;
      letter-spacing: .04rem;
    }
    h2 {
      color: #FFF;
    }
    .title {
      margin-bottom: 5vh;
    }
  }

  p {
// max-width: 8 cols;
  }
  h3 {

  }
  &__scroll {
    border-radius: 4px;
    background-color: #626469;
    // box-shadow: 0px 18px 24px 0px rgba(0, 0, 0, .04);
    width: 40px;
    height: 80px;
    display: block;
    position: absolute;
    bottom: -45px;
    right: 0;
    background-image: url('../assets/Arrow.png');
    background-repeat: no-repeat;
    background-position: center center;
    cursor: pointer;
    z-index: 9;
    @media (min-width: 768px) {
      bottom: -30px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/index.vue